import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFloat from "@/components/WhatsAppFloat";
import { getBlogBySlug } from "@/lib/api";
import { Calendar, ArrowLeft, MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { waLink } from "@/constants/site";
import useSEO from "@/hooks/useSEO";
import { IONA } from "@/constants/site";

export default function BlogDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [blog, setBlog] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useSEO({
    title: blog
      ? `${blog.title} · İONA Ümraniye Güzellik Merkezi Blog`
      : "Blog · İONA Güzellik Merkezi",
    description: blog ? blog.excerpt : undefined,
    canonical: `${IONA.siteUrl}/blog/${slug}`,
    image: blog && blog.cover_image ? blog.cover_image : undefined,
  });

  useEffect(() => {
    setLoading(true);
    setNotFound(false);
    getBlogBySlug(slug)
      .then(setBlog)
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
  }, [slug]);

  return (
    <div data-testid="blog-detail-page" className="min-h-screen bg-white">
      <Navbar />
      <main className="pt-32 pb-20">
        <article className="iona-container max-w-3xl">
          <button
            onClick={() => navigate("/blog")}
            data-testid="back-to-blog"
            className="inline-flex items-center gap-2 text-[#5E2A84] hover:gap-3 transition-all text-sm font-medium mb-10"
          >
            <ArrowLeft className="w-4 h-4" />
            Tüm Yazılara Dön
          </button>

          {loading ? (
            <div className="space-y-6">
              <div className="h-12 bg-[#F4F1F9] rounded animate-pulse" />
              <div className="h-80 bg-[#F4F1F9] rounded-3xl animate-pulse" />
              <div className="h-4 bg-[#F4F1F9] rounded animate-pulse w-3/4" />
              <div className="h-4 bg-[#F4F1F9] rounded animate-pulse" />
              <div className="h-4 bg-[#F4F1F9] rounded animate-pulse w-5/6" />
            </div>
          ) : notFound || !blog ? (
            <div className="py-20 text-center">
              <h1 className="font-serif text-3xl text-[#1A1A1A] mb-4">
                Yazı bulunamadı
              </h1>
              <Link to="/blog" className="text-[#5E2A84] hover:underline">
                Tüm yazılara dön
              </Link>
            </div>
          ) : (
            <>
              <div className="flex items-center gap-3 text-xs text-[#4A4A4A] mb-6">
                <span className="px-3 py-1 rounded-full bg-[#F4F1F9] text-[#5E2A84] uppercase tracking-wider">
                  {blog.category}
                </span>
                <span className="flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" />
                  {new Date(blog.created_at).toLocaleDateString("tr-TR", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </span>
              </div>
              <h1
                data-testid="blog-detail-title"
                className="font-serif text-3xl sm:text-4xl lg:text-5xl text-[#1A1A1A] leading-[1.1] tracking-tight mb-6"
              >
                {blog.title}
              </h1>
              <p className="text-lg text-[#4A4A4A] leading-relaxed mb-10">
                {blog.excerpt}
              </p>

              {blog.cover_image && (
                <div className="rounded-3xl overflow-hidden mb-12 iona-soft-shadow">
                  <img
                    src={blog.cover_image}
                    alt={blog.title}
                    className="w-full h-auto max-h-[520px] object-cover"
                  />
                </div>
              )}

              <div className="prose prose-lg max-w-none text-[#1A1A1A] leading-relaxed whitespace-pre-wrap font-[Outfit]">
                {blog.content.split("\n").map((para, i) => (
                  <p key={i} className="mb-5 text-[#1A1A1A] leading-[1.85]">
                    {para}
                  </p>
                ))}
              </div>

              <div className="mt-16 p-8 md:p-10 rounded-3xl bg-[#F4F1F9] text-center">
                <h3 className="font-serif text-2xl text-[#1A1A1A] mb-3">
                  Sorularınız mı var?
                </h3>
                <p className="text-[#4A4A4A] mb-7 max-w-md mx-auto">
                  Ücretsiz danışmanlık ve deneme seansı için bize WhatsApp'tan
                  yazabilirsiniz.
                </p>
                <a
                  href={waLink(`Merhaba, "${blog.title}" yazınızı inceledim, randevu almak istiyorum.`)}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="blog-detail-whatsapp-cta"
                >
                  <Button className="h-13 px-7 py-3 rounded-full bg-[#5E2A84] hover:bg-[#4a2168] text-white iona-soft-shadow">
                    <MessageCircle className="w-4 h-4 mr-2" />
                    WhatsApp'tan Randevu Al
                  </Button>
                </a>
              </div>
            </>
          )}
        </article>
      </main>
      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
